/**
 * Agenda — vue mensuelle et liste chronologique.
 *
 * Les **périodes de vacances scolaires apparaissent en fond du calendrier** :
 * pour une famille, c'est une information structurante. Savoir qu'une échéance
 * tombe pendant les vacances change la façon de s'organiser.
 *
 * Les pastilles sous les jours chargés reprennent les couleurs de catégorie, en
 * petites surfaces comme le veut le système.
 */

import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import {
  type PeriodeVacances,
  type Tache,
  MOIS_LONG,
  ajouteJours,
  aujourdhuiParis,
  dateIso,
  enClairAvecJour,
  joursDansMois,
  jourSemaine,
  occurrencesEntre,
  parties,
  periodeDe,
} from '@foyer/noyau';

import { CarteTache, Chargement, Erreur, Section, TRAIT } from '../composants/ui.js';
import { COULEUR_CATEGORIE } from '../composants/ui.js';
import { useAppli, useRessource } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';
import { FeuilleTache } from './FeuilleTache.js';

const JOURS_COURTS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

export function Agenda() {
  const { rafraichis } = useAppli();
  const aujourdhui = aujourdhuiParis();
  const taches = useRessource(() => donnees.taches({ statut: 'active' }));
  const vacances = useRessource(() => donnees.vacances());

  const [curseur, setCurseur] = useState(() => {
    const { annee, mois } = parties(aujourdhui);
    return { annee, mois };
  });
  const [selection, setSelection] = useState<string | null>(aujourdhui);
  const [vue, setVue] = useState<'mois' | 'liste'>('mois');
  const [feuille, setFeuille] = useState<Tache | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const liste = (taches.donnee?.taches ?? []).filter((t) => t.echeance);
  const periodes: PeriodeVacances[] = vacances.donnee?.periodes ?? [];

  const grille = useMemo(() => {
    const { annee, mois } = curseur;
    const premier = dateIso(annee, mois, 1);
    // La semaine commence le lundi : on remonte au lundi précédant le 1er.
    const debut = ajouteJours(premier, 1 - jourSemaine(premier));
    const cases: string[] = [];
    for (let i = 0; i < 42; i++) cases.push(ajouteJours(debut, i));
    // On coupe la dernière ligne si elle est entièrement hors du mois.
    const utiles = cases.filter((_, i) => i < 35 || cases.slice(35).some((d) => parties(d).mois === mois));
    return utiles;
  }, [curseur]);

  /**
   * Une occurrence : la tâche, et le jour où elle tombe. `projetee` distingue la
   * répétition à venir de l'échéance en cours.
   *
   * La distinction n'est pas décorative. On ne coche que ce qui est dû : cocher
   * la répétition de novembre alors que celle d'août n'est pas faite n'aurait
   * pas de sens. Les occurrences projetées montrent donc le rythme sans être
   * actionnables.
   */
  const parJour = useMemo(() => {
    const carte = new Map<string, { tache: Tache; date: string; projetee: boolean }[]>();
    if (grille.length === 0) return carte;
    const debut = grille[0]!;
    const fin = grille[grille.length - 1]!;

    for (const t of liste) {
      const dates = occurrencesEntre(
        {
          rrule: t.rrule,
          depart: t.recurrenceDepart,
          base: t.recurrenceBase,
          ancrage: t.ancrageVacances,
        },
        t.echeance!,
        debut,
        fin,
        periodes,
      );
      for (const d of dates) {
        carte.set(d, [...(carte.get(d) ?? []), { tache: t, date: d, projetee: d !== t.echeance }]);
      }
    }
    return carte;
  }, [liste, grille, periodes]);

  function deplace(delta: number) {
    setCurseur(({ annee, mois }) => {
      const total = annee * 12 + (mois - 1) + delta;
      return { annee: Math.floor(total / 12), mois: (total % 12) + 1 };
    });
  }

  async function coche(t: Tache) {
    setOccupe(t.id);
    try {
      await donnees.coche(t.id, t.titre);
      rafraichis();
    } finally {
      setOccupe(null);
    }
  }

  /**
   * Vue liste : les douze prochains mois, répétitions comprises. Se limiter aux
   * échéances courantes donnait une liste trompeuse — une tâche mensuelle y
   * figurait une fois, comme une tâche ponctuelle.
   *
   * **Ce `useMemo` doit rester au-dessus des sorties conditionnelles.** Il était
   * en dessous : au premier rendu, `chargement` étant vrai, le composant sortait
   * tôt et le hook n'était jamais appelé ; au rendu suivant il l'était. React
   * lève alors « Rendered more hooks than during the previous render », démonte
   * l'arbre, et dans la WebView de l'application cela se voit comme un plantage.
   * L'écran était donc inaccessible sur mobile à chaque ouverture.
   */
  const aVenir = useMemo(() => {
    const fin = ajouteJours(aujourdhui, 365);
    const out: { tache: Tache; date: string; projetee: boolean }[] = [];
    for (const t of liste) {
      const dates = occurrencesEntre(
        { rrule: t.rrule, depart: t.recurrenceDepart, base: t.recurrenceBase, ancrage: t.ancrageVacances },
        t.echeance!,
        aujourdhui,
        fin,
        periodes,
      );
      for (const d of dates) out.push({ tache: t, date: d, projetee: d !== t.echeance });
    }
    return out.sort((a, b) => a.date.localeCompare(b.date)).slice(0, 60);
  }, [liste, periodes, aujourdhui]);

  if (taches.chargement && !taches.donnee) return <Chargement quoi="Chargement de l’agenda" />;
  if (taches.erreur && !taches.donnee) return <Erreur message={taches.erreur} />;

  const duJour = selection ? (parJour.get(selection) ?? []) : [];

  return (
    <>
      <div className="entete-vue">
        <h1>
          Agenda
        </h1>
        <div className="puces" style={{ padding: 0 }}>
          <button
            type="button"
            className={`puce${vue === 'mois' ? ' active' : ''}`}
            onClick={() => setVue('mois')}
          >
            Mois
          </button>
          <button
            type="button"
            className={`puce${vue === 'liste' ? ' active' : ''}`}
            onClick={() => setVue('liste')}
          >
            Liste
          </button>
        </div>
      </div>

      {vue === 'mois' ? (
        <>
          <div className="entre-deux" style={{ marginBottom: 10 }}>
            <button type="button" className="bouton icone" onClick={() => deplace(-1)} aria-label="Mois précédent">
              <ChevronLeft strokeWidth={TRAIT} size={20} aria-hidden />
            </button>
            <strong>
              {MOIS_LONG[curseur.mois - 1]} {curseur.annee}
            </strong>
            <button type="button" className="bouton icone" onClick={() => deplace(1)} aria-label="Mois suivant">
              <ChevronRight strokeWidth={TRAIT} size={20} aria-hidden />
            </button>
          </div>

          <div className="mois" role="grid" aria-label={`${MOIS_LONG[curseur.mois - 1]} ${curseur.annee}`}>
            {JOURS_COURTS.map((j, i) => (
              <div key={i} className="entete-jour">
                {j}
              </div>
            ))}
            {grille.map((jour) => {
              const dansLeMois = parties(jour).mois === curseur.mois;
              const duJourLa = parJour.get(jour) ?? [];
              const vacance = periodeDe(jour, periodes);
              const classes = ['jour'];
              if (!dansLeMois) classes.push('hors');
              if (vacance) classes.push('vacances');
              if (jour === aujourdhui) classes.push('aujourdhui');
              if (jour === selection) classes.push('selectionne');
              return (
                <button
                  key={jour}
                  type="button"
                  className={classes.join(' ')}
                  onClick={() => setSelection(jour)}
                  aria-label={`${enClairAvecJour(jour)}${vacance ? `, ${vacance.description}` : ''}${
                    duJourLa.length ? `, ${duJourLa.length} échéance(s)` : ''
                  }`}
                  title={vacance?.description}
                >
                  {parties(jour).jour}
                  <span className="pastilles">
                    {[...new Set(duJourLa.map((o) => o.tache.categorie))].slice(0, 4).map((c) => (
                      <i key={c} style={{ background: COULEUR_CATEGORIE[c] }} />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>

          {periodes.length === 0 && (
            <p className="sourdine" style={{ marginTop: 10 }}>
              Le calendrier scolaire n’est pas encore synchronisé : les périodes de vacances
              n’apparaissent pas en fond. Écran Famille pour le récupérer.
            </p>
          )}

          {selection && (
            <>
              <div className="section-titre" style={{ cursor: 'default' }}>
                {enClairAvecJour(selection)}
                {periodeDe(selection, periodes) && (
                  <span className="compte">{periodeDe(selection, periodes)!.description}</span>
                )}
              </div>
              {duJour.length === 0 ? (
                <div className="note">Rien ce jour-là.</div>
              ) : (
                duJour.map((o) => (
                  <CarteTache
                    key={o.tache.id + o.date}
                    // La carte porte la date de l'occurrence, pas l'échéance de
                    // la tâche : « dans deux mois » doit se lire depuis le jour
                    // affiché.
                    tache={o.projetee ? { ...o.tache, echeance: o.date } : o.tache}
                    aujourdhui={aujourdhui}
                    occupe={occupe === o.tache.id}
                    // Une répétition à venir ne se coche pas : seule l'échéance
                    // en cours est due.
                    onCoche={o.projetee ? undefined : () => void coche(o.tache)}
                    onOuvre={() => setFeuille(o.tache)}
                  />
                ))
              )}
            </>
          )}
        </>
      ) : (
        <Section titre="Les prochaines échéances" compte={aVenir.length}>
          {aVenir.map((o) => (
            <CarteTache
              key={o.tache.id + o.date}
              tache={o.projetee ? { ...o.tache, echeance: o.date } : o.tache}
              aujourdhui={aujourdhui}
              occupe={occupe === o.tache.id}
              onCoche={o.projetee ? undefined : () => void coche(o.tache)}
              onOuvre={() => setFeuille(o.tache)}
            />
          ))}
        </Section>
      )}

      {feuille && (
        <FeuilleTache tache={feuille} periodes={periodes} onFerme={() => setFeuille(null)} />
      )}
    </>
  );
}

/** Sous-liste par mois, réutilisée par l'écran Administratif. */
export function joursDuMoisCourant(annee: number, mois: number): string[] {
  const total = joursDansMois(annee, mois);
  return Array.from({ length: total }, (_, i) => dateIso(annee, mois, i + 1));
}
